import { useState } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { ApiRequestError, customersApi } from '@/api';
import { Button, Panel, TextField } from '@/components/ui';
import styles from '@/styles/components/NewCustomerForm/style.scss';

interface NewCustomerFormProps {
  /** Pre-fills Name or Mobile from what was searched. */
  initialQuery: string;
  onCreated: (customer: CustomerDTO) => void;
  onCancel: () => void;
}

const digitsOnly = (v: string) => v.replace(/\D/g, '');

function prefill(query: string) {
  const q = query.trim();
  const isNumber = /^[\d\s+-]+$/.test(q) && digitsOnly(q).length >= 3;
  return isNumber ? { name: '', phone: digitsOnly(q).slice(-10) } : { name: q, phone: '' };
}

export function NewCustomerForm({ initialQuery, onCreated, onCancel }: NewCustomerFormProps) {
  const [initial] = useState(() => prefill(initialQuery));
  const [name, setName] = useState(initial.name);
  const [phone, setPhone] = useState(initial.phone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validPhone = /^[6-9]\d{9}$/.test(phone);
  const valid = name.trim().length > 0 && validPhone;
  const hint = error ?? (!name.trim() ? 'Enter a name' : !validPhone ? 'Enter a 10-digit mobile number' : undefined);

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      onCreated(await customersApi.create({ name: name.trim(), phone }));
    } catch (err) {
      // Mobile already registered: use the existing customer.
      if (err instanceof ApiRequestError && err.status === 409 && err.details) {
        onCreated(err.details as CustomerDTO);
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not save customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel tone="primary">
      <div role="group" aria-label="New customer" className={styles.form}>
        <span className={styles.title}>New customer</span>
        <div className={styles.fields}>
          <TextField label="Name" placeholder="Customer name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus={!initial.name} />
          <TextField
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            placeholder="10-digit number"
            prefix="+91"
            mono
            value={phone}
            onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 10))}
            autoFocus={!!initial.name}
          />
        </div>
        {hint && <span className={error ? styles.error : styles.hint}>{hint}</span>}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save customer'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
